import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Heading } from "react-aria-components";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { useTRPC } from "@/integrations/trpc/react";

export type ToggleRosterPublishedFormProps = {
  tournamentId: number;
  division: { id: number; rosterPublished: boolean };
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ToggleRosterPublishedForm({
  tournamentId,
  division,
  onOpenChange,
  ...props
}: ToggleRosterPublishedFormProps) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const nextPublished = !division.rosterPublished;

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.toggleRosterPublished.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());

      onOpenChange(false);
    },
  });

  const form = useAppForm({
    defaultValues: {},
    onSubmit: () => {
      mutate({
        tournamentDivisionId: division.id,
        published: nextPublished,
      });
    },
  });

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          {nextPublished ? "Publish Roster" : "Unpublish Roster"}
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          {nextPublished
            ? "Publishing the roster will make teams, pools, games, and playoffs visible to all users."
            : "Unpublishing the roster will hide teams, pools, games, and playoffs from non-admin users."}
        </p>

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title="Unable to update roster"
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton requireChange={false}>
                {nextPublished ? "Publish" : "Unpublish"}
              </form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
